import React, { useMemo } from "react";
import { Box, Card, Tooltip, Typography } from "@mui/material";

// Mini-sparkline that occupies the icon's slot in the title row. No axes,
// no labels, just a polyline + faint area fill. Sized to fit alongside
// the title without growing the card height.
function MiniSparkline({ values, color }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(1, ...values);
  const w = 100;
  const h = 36;
  const stepX = w / Math.max(1, values.length - 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = h - (v / max) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M ${points.join(" L ")}`;
  const fillPath = `${linePath} L ${w},${h} L 0,${h} Z`;
  const id = `mspk-${color.replace("#", "")}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{
        width: "clamp(76px, 8vw, 120px)",
        height: "clamp(28px, 3vw, 44px)",
        display: "block",
      }}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${id})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Color thresholds for the success-rate KPI.
function rateColor(pct) {
  if (pct >= 98) return "#16a34a"; // green
  if (pct >= 90) return "#f59e0b"; // amber
  return "#dc2626"; // red
}

// Trend chip: failures going UP is BAD (red), DOWN is GOOD (green).
// Flat = neutral grey. Mirrors the user-portal DeltaIndicator pattern.
function trendInfo(today, yesterday) {
  if (yesterday === 0 && today === 0) {
    return { arrow: "—", label: "no change", color: "#94a3b8" };
  }
  if (yesterday === 0) {
    return { arrow: "▲", label: `${today} new`, color: "#dc2626" };
  }
  const diff = today - yesterday;
  const pct = Math.round((diff / yesterday) * 100);
  if (pct === 0) return { arrow: "—", label: "0%", color: "#94a3b8" };
  return {
    arrow: pct > 0 ? "▲" : "▼",
    label: `${Math.abs(pct)}%`,
    color: pct > 0 ? "#dc2626" : "#16a34a",
  };
}

const FALLBACK = {
  attempts: 0,
  passed: 0,
  failed: 0,
  bailed: 0,
  rate_pct: 0,
  yesterday_failed: 0,
  sparkline: [],
};

function IvrAuthCard({ data }) {
  const safe = data || FALLBACK;
  const {
    attempts,
    passed,
    failed,
    bailed,
    rate_pct: rateRaw,
    yesterday_failed,
    sparkline,
  } = safe;
  const hasSpark = Array.isArray(sparkline) && sparkline.some((v) => v > 0);
  const rate = Number(rateRaw) || 0;
  const rateColored = rateColor(rate);
  const trend = useMemo(() => trendInfo(failed, yesterday_failed), [failed, yesterday_failed]);

  const noActivity = attempts === 0;
  const accent = noActivity ? "#94a3b8" : rateColored;

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        // Same padding profile as StatCard so heights match.
        p: "clamp(0.55rem, 1.1vw, 1rem)",
        borderRadius: "20px",
        backgroundColor: "#fff",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 8px 32px ${accent}22`,
        border: `1px solid ${accent}22`,
        transition: "all 0.35s ease",
        cursor: "default",
        minWidth: 0,

        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: `0 20px 40px ${accent}33`,
        },

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "6px",
          height: "100%",
          backgroundColor: accent,
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
        },
      }}
    >
      {/* Two-row layout, mirroring StatCard structure:
          Row 1: Title (full width) + icon at right
          Row 2: rate + failures, side-by-side using the 2-col extra width */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "clamp(2px, 0.4vw, 6px)",
          minWidth: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "clamp(0.7rem, 0.95vw, 0.85rem)",
              fontWeight: 700,
              textTransform: "capitalize",
              color: accent,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ".App.user_theme_white &": { color: "#0f172a !important" },
            }}
          >
            IVR Authentication
          </Typography>
          {/* Mini-sparkline (last-24h attempts) sits where the icon used
              to live. Same vertical slot — card height unchanged. Falls
              back to nothing if no 24h activity. */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              minWidth: "clamp(76px, 8vw, 120px)",
            }}
          >
            {hasSpark ? (
              <Tooltip title="Last 24h attempts" arrow>
                <Box sx={{ display: "flex" }}>
                  <MiniSparkline values={sparkline} color={accent} />
                </Box>
              </Tooltip>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  color: "#94a3b8",
                  fontStyle: "italic",
                  whiteSpace: "nowrap",
                }}
              >
                no 24h data
              </Typography>
            )}
          </Box>
        </Box>

        {noActivity ? (
          <Typography
            sx={{
              fontSize: "clamp(0.95rem, 1.7vw, 1.4rem)",
              fontWeight: 700,
              color: "#475569",
              lineHeight: 1.15,
            }}
          >
            No IVR attempts today
          </Typography>
        ) : (
          // Two-column inner layout for the 2-wide card: rate+subtext on
          // left, failures+trend on right. Single visual row, height tracks
          // StatCard's value-row dimensions.
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "clamp(1.1rem, 2.2vw, 1.9rem)",
                  fontWeight: 700,
                  color: rateColored,
                  lineHeight: 1.15,
                }}
              >
                {rate.toFixed(1)}%
              </Typography>
              <Typography
                sx={{
                  fontSize: "clamp(0.78rem, 0.95vw, 0.9rem)",
                  fontWeight: 500,
                  color: "#475569",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mt: 0.2,
                }}
                title={`${passed.toLocaleString()} successful out of ${attempts.toLocaleString()} attempts`}
              >
                {passed.toLocaleString()}/{attempts.toLocaleString()} attempts
                {bailed > 0 && (
                  <span style={{ color: "#94a3b8" }}>
                    {" · "}
                    {bailed} incomplete
                  </span>
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.3,
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "clamp(0.78rem, 1vw, 0.95rem)",
                  fontWeight: 700,
                  color: "#1f2937",
                  lineHeight: 1.15,
                }}
              >
                Failures:{" "}
                <Box component="span" sx={{ color: "#dc2626 !important" }}>
                  {failed}
                </Box>
              </Typography>
              <Tooltip
                title={`Yesterday: ${yesterday_failed} · Today: ${failed}`}
                arrow
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                    px: 0.7,
                    py: 0.05,
                    borderRadius: "8px",
                    background: `${trend.color}14`,
                    border: `1px solid ${trend.color}33`,
                    cursor: "help",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.66rem",
                      fontWeight: 700,
                      color: trend.color,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {trend.arrow} {trend.label} vs yesterday
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default IvrAuthCard;
